/** biome-ignore-all lint/suspicious/noArrayIndexKey: option editor rows use index-based keys before persistence */
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  ScrollArea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  toast,
} from "@devhop/ui";
import { PlusIcon, TrashIcon } from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { validateEffectiveField } from "@/modules/forms/domain/contracts/forms";
import { isFormFieldOptionEnabled } from "@/modules/forms/domain/lib/form-field-options";
import { useUpdateFormFieldMutation } from "@/modules/forms/presentation/api/queries";
import type { FormFieldDTO } from "@/modules/forms/presentation/api/types";
import { FormSectionRichTextEditor } from "@/modules/forms/presentation/ui/builder/FormSectionRichTextEditor";
import { FORM_FIELD_TYPE_LABEL_LA } from "@/modules/forms/presentation/ui/builder/form-field-defaults";
import { RepeaterFieldSettingsEditor } from "@/modules/forms/presentation/ui/builder/RepeaterFieldSettingsEditor";
import {
  defaultRepeaterSettings,
  parseRepeaterSettings,
  repeaterSettingsToRecord,
} from "@/modules/forms/domain/lib/form-repeater-settings";
import type { FormFieldOptionItem, RepeaterSettings } from "@/server/platform/db/schema/forms";
import {
  type FormFieldType,
  formFieldTypes,
} from "@/server/platform/db/schema/forms";

const OPTIONS_TYPES = new Set<FormFieldType>([
  "choice",
  "dropdown",
  "checkbox",
]);

type FieldEditDialogProps = {
  formId: string;
  field: FormFieldDTO | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hasResponses: boolean;
};

function emptyToNull(s: string): string | null {
  const t = s.trim();
  return t.length ? t : null;
}

function sectionHtmlToNullable(html: string): string | null {
  const visible = html
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return visible.length ? html.trim() : null;
}

function buildNumberFieldSettings(
  numMinStr: string,
  numMaxStr: string,
  numberIntegerOnly: boolean,
): Record<string, unknown> | null {
  const out: Record<string, unknown> = {};
  if (numberIntegerOnly) out.integerOnly = true;
  const minT = numMinStr.trim();
  if (minT.length > 0) {
    const n = Number(minT);
    if (Number.isFinite(n)) out.min = n;
  }
  const maxT = numMaxStr.trim();
  if (maxT.length > 0) {
    const n = Number(maxT);
    if (Number.isFinite(n)) out.max = n;
  }
  return Object.keys(out).length > 0 ? out : null;
}

export function FieldEditDialog({
  formId,
  field,
  open,
  onOpenChange,
  hasResponses,
}: FieldEditDialogProps) {
  const updateField = useUpdateFormFieldMutation(formId);

  /** hydrate ຈາກ field ຕະຫຼາດຄັ້ງເມື່ອເປີດ dialog ຫຼື ປ່ຽນ field.id — ບໍ່ເຮັດເມື່ອ refetch ສົ່ງ object ໃໝ່ຄ່າເດີມ */
  const hydratedFieldIdRef = useRef<string | null>(null);
  const tabInBackgroundRef = useRef(false);

  useEffect(() => {
    const onVis = () => {
      tabInBackgroundRef.current = document.visibilityState === "hidden";
    };
    onVis();
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  const handleDialogOpenChange = (next: boolean) => {
    if (!next && tabInBackgroundRef.current) {
      return;
    }
    onOpenChange(next);
  };

  const [type, setType] = useState<FormFieldType>("text");
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [placeholder, setPlaceholder] = useState("");
  const [required, setRequired] = useState(false);
  const [options, setOptions] = useState<FormFieldOptionItem[]>([]);
  const [maxRating, setMaxRating] = useState<5 | 10>(5);
  const [textareaRows, setTextareaRows] = useState(4);
  const [numMinStr, setNumMinStr] = useState("");
  const [numMaxStr, setNumMaxStr] = useState("");
  const [numberIntegerOnly, setNumberIntegerOnly] = useState(false);
  const [repeaterSettings, setRepeaterSettings] = useState<RepeaterSettings>(
    defaultRepeaterSettings(),
  );

  useLayoutEffect(() => {
    if (!open) {
      hydratedFieldIdRef.current = null;
      return;
    }
    if (!field) return;

    if (hydratedFieldIdRef.current === field.id) {
      return;
    }
    hydratedFieldIdRef.current = field.id;

    setType(field.type);
    setLabel(field.label);
    setDescription(field.description ?? "");
    setPlaceholder(field.placeholder ?? "");
    setRequired(field.type === "section" ? false : field.required);
    setOptions(Array.isArray(field.options) ? [...field.options] : []);
    const mr =
      typeof field.settings === "object" &&
      field.settings &&
      field.settings.maxRating === 10
        ? 10
        : 5;
    setMaxRating(mr);
    const rows =
      typeof field.settings === "object" &&
      field.settings &&
      typeof field.settings.rows === "number"
        ? field.settings.rows
        : 4;
    setTextareaRows(rows);
    const mn =
      typeof field.settings === "object" &&
      field.settings &&
      typeof field.settings.min === "number" &&
      Number.isFinite(field.settings.min)
        ? String(field.settings.min)
        : "";
    setNumMinStr(mn);
    const mx =
      typeof field.settings === "object" &&
      field.settings &&
      typeof field.settings.max === "number" &&
      Number.isFinite(field.settings.max)
        ? String(field.settings.max)
        : "";
    setNumMaxStr(mx);
    setNumberIntegerOnly(
      typeof field.settings === "object" &&
        field.settings !== null &&
        field.settings.integerOnly === true,
    );
    setRepeaterSettings(
      parseRepeaterSettings(field.settings) ?? defaultRepeaterSettings(),
    );
  }, [open, field]);

  const needsOptions = OPTIONS_TYPES.has(type);
  const isSection = type === "section";

  const patchOptionAt = <K extends keyof FormFieldOptionItem>(
    idx: number,
    key: K,
    val: FormFieldOptionItem[K],
  ) =>
    setOptions((prev) =>
      prev.map((o, i) => (i === idx ? { ...o, [key]: val } : o)),
    );

  async function handleSave() {
    if (!field) return;

    const trimmed = label.trim();
    if (!trimmed) {
      toast.error("ປ້ອນຫົວຂໍ້");
      return;
    }

    const descForApi = isSection
      ? sectionHtmlToNullable(description)
      : emptyToNull(description);

    const numberSettings =
      type === "number"
        ? buildNumberFieldSettings(numMinStr, numMaxStr, numberIntegerOnly)
        : null;

    const repeaterSettingsRecord =
      type === "repeater" ? repeaterSettingsToRecord(repeaterSettings) : null;

    const eff = validateEffectiveField({
      type,
      label: trimmed,
      description: descForApi,
      placeholder: isSection ? null : emptyToNull(placeholder),
      required: isSection ? false : required,
      options: needsOptions ? options : null,
      settings:
        type === "rating"
          ? { maxRating }
          : type === "textarea"
            ? { rows: textareaRows }
            : type === "number"
              ? numberSettings
              : type === "repeater"
                ? repeaterSettingsRecord
                : null,
    });
    if (eff) {
      toast.error(
        eff === "OPTIONS_NONE_ENABLED"
          ? "ຕ້ອງມີຢ່າງໜ້ອຍ 1 ທາງເລືອກທີ່ເປີດຮັບເລືອກ"
          : eff === "REPEATER_SETTINGS_INVALID"
            ? "ການຕັ້ງຄ່າ repeater ບໍ່ຖືກຕ້ອງ"
            : eff === "REPEATER_ITEM_KEY_DUPLICATE"
              ? "key ຂອງຟິວໃນ repeater ຊ້ຳກັນ"
              : eff === "REPEATER_ITEM_OPTIONS_REQUIRED"
                ? "ຟິວ choice ຕ້ອງມີທາງເລືອກ"
                : eff,
      );
      return;
    }

    await updateField.mutateAsync({
      fieldId: field.id,
      body: {
        ...(!hasResponses && type !== field.type ? { type } : {}),
        label: trimmed,
        description: descForApi,
        placeholder: isSection ? null : emptyToNull(placeholder),
        required: isSection ? false : required,
        options: needsOptions ? options : null,
        settings:
          type === "rating"
            ? { maxRating }
            : type === "textarea"
              ? { rows: textareaRows }
              : type === "number"
                ? numberSettings
                : type === "repeater"
                  ? repeaterSettingsRecord
                  : null,
      },
    });
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent
        showCloseButton
        className="flex max-h-[min(90vh,920px)] min-h-0 flex-col gap-4 overflow-hidden sm:max-w-2xl"
      >
        <DialogHeader className="shrink-0">
          <DialogTitle>ແກ້ໄຂຟິວ</DialogTitle>
        </DialogHeader>
        {field ? (
          <>
            <ScrollArea className="max-h-[calc(90vh-11rem)] min-h-0 w-full flex-1 overflow-auto pr-4">
              <div className="flex flex-col gap-4 pb-4">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="field-label">ຫົວຂໍ້</Label>
                  <Input
                    id="field-label"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Label
                    htmlFor={
                      isSection ? "field-section-body" : "field-description"
                    }
                  >
                    {isSection ? "ເນື້ອຫາອະທິບາຍ (ຂໍ້ຄວາມຮ່ວມ)" : "ອະທິບາຍ"}
                  </Label>
                  {isSection && field ? (
                    <FormSectionRichTextEditor
                      resetKey={field.id}
                      initialHtml={description}
                      onHtmlChange={setDescription}
                      disabled={updateField.isPending}
                    />
                  ) : (
                    <Textarea
                      id="field-description"
                      rows={3}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                    />
                  )}
                </div>
                {!isSection ? (
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="field-placeholder">ຕົວຢ່າງ</Label>
                    <Input
                      id="field-placeholder"
                      value={placeholder}
                      onChange={(e) => setPlaceholder(e.target.value)}
                    />
                  </div>
                ) : null}
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="field-required"
                    checked={required}
                    disabled={isSection}
                    onCheckedChange={(v) => setRequired(Boolean(v))}
                  />
                  <Label htmlFor="field-required">ຈຳເປັນ</Label>
                </div>
                <div className="flex flex-col gap-2">
                  <Label>ປະເພດ</Label>
                  <Select
                    value={type}
                    disabled={hasResponses}
                    onValueChange={(v) => setType(v as FormFieldType)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="ເລືອກປະເພດ" />
                    </SelectTrigger>
                    <SelectContent>
                      {formFieldTypes.map((t) => (
                        <SelectItem key={t} value={t}>
                          {FORM_FIELD_TYPE_LABEL_LA[t]} ({t})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {hasResponses ? (
                    <p className="text-muted-foreground text-xs">
                      ມີຄຳຕອບແລ້ວ — ບໍ່ສາມາດປ່ຽນປະເພດຟິວ.
                    </p>
                  ) : null}
                </div>
                {type === "rating" ? (
                  <div className="flex flex-col gap-2">
                    <Label>ຕາກຄະແນນສູງສຸດ</Label>
                    <Select
                      value={String(maxRating)}
                      onValueChange={(v) => setMaxRating(v === "10" ? 10 : 5)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="5">5</SelectItem>
                        <SelectItem value="10">10</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ) : null}
                {type === "textarea" ? (
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="field-rows">ຈໍານວນແຖວ</Label>
                    <Input
                      id="field-rows"
                      type="number"
                      min={2}
                      max={40}
                      value={textareaRows}
                      onChange={(e) =>
                        setTextareaRows(
                          Number.parseInt(e.target.value, 10) || 4,
                        )
                      }
                    />
                  </div>
                ) : null}
                {type === "number" ? (
                  <>
                    <div className="flex flex-col gap-2">
                      <Label htmlFor="field-num-min">ຄ່າຕໍາເໜ່ອ (ບໍ່ບັງຄັບ)</Label>
                      <Input
                        id="field-num-min"
                        type="text"
                        inputMode="decimal"
                        value={numMinStr}
                        onChange={(e) => setNumMinStr(e.target.value)}
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <Label htmlFor="field-num-max">ຄ່າສູງສຸດ (ບໍ່ບັງຄັບ)</Label>
                      <Input
                        id="field-num-max"
                        type="text"
                        inputMode="decimal"
                        value={numMaxStr}
                        onChange={(e) => setNumMaxStr(e.target.value)}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="field-num-integer"
                        checked={numberIntegerOnly}
                        onCheckedChange={(v) =>
                          setNumberIntegerOnly(Boolean(v))
                        }
                      />
                      <Label
                        htmlFor="field-num-integer"
                        className="font-normal"
                      >
                        ຮັບສະເພາະຈຳນວນເຕັມ
                      </Label>
                    </div>
                  </>
                ) : null}
                {needsOptions ? (
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center justify-between">
                      <Label>ທາງເລືອກຂໍ້ຕອບ</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setOptions((prev) => [
                            ...prev,
                            {
                              value: `v_${Date.now().toString(36)}`,
                              label: `ທາງເລືອກ ${prev.length + 1}`,
                              enabled: true,
                            },
                          ])
                        }
                      >
                        <PlusIcon className="mr-1 h-4 w-4" />
                        ເພີ່ມທາງເລືອກ
                      </Button>
                    </div>
                    <p className="text-muted-foreground text-xs">
                      ປິດທາງເລືອກເພື່ອບໍ່ໃຫ້ເລືອກໃນຟອມ public — ຄຳຕອບເກົ່າຍັງຄົງຢູ່.
                    </p>
                    <div className="flex flex-col gap-2">
                      {options.map((o, idx) => {
                        const optionOpen = isFormFieldOptionEnabled(o);
                        return (
                          <div
                            key={idx}
                            className={`flex flex-col gap-2 rounded-md border p-2 ${optionOpen ? "" : "border-dashed bg-muted/30 opacity-80"}`}
                          >
                            <div className="flex flex-wrap items-start gap-2">
                              <Input
                                className="min-w-[6rem] flex-1"
                                placeholder="value"
                                value={o.value}
                                onChange={(e) =>
                                  patchOptionAt(idx, "value", e.target.value)
                                }
                              />
                              <Input
                                className="min-w-[6rem] flex-1"
                                placeholder="ປ້າຍທາງເລືອກ"
                                value={o.label}
                                onChange={(e) =>
                                  patchOptionAt(idx, "label", e.target.value)
                                }
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                aria-label="ລຶບທາງເລືອກ"
                                onClick={() =>
                                  setOptions((prev) =>
                                    prev.filter((_, i) => i !== idx),
                                  )
                                }
                              >
                                <TrashIcon className="h-4 w-4" />
                              </Button>
                            </div>
                            <div className="flex items-center gap-2">
                              <Checkbox
                                id={`field-opt-enabled-${idx}`}
                                checked={optionOpen}
                                onCheckedChange={(v) =>
                                  patchOptionAt(idx, "enabled", Boolean(v))
                                }
                              />
                              <Label
                                htmlFor={`field-opt-enabled-${idx}`}
                                className="font-normal text-xs"
                              >
                                ເປີດຮັບເລືອກໃນຟອມ
                              </Label>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
                {type === "repeater" ? (
                  <RepeaterFieldSettingsEditor
                    value={repeaterSettings}
                    onChange={setRepeaterSettings}
                    disabled={updateField.isPending}
                  />
                ) : null}
              </div>
            </ScrollArea>
            <DialogFooter className="shrink-0 gap-2">
              <Button
                variant="outline"
                type="button"
                onClick={() => handleDialogOpenChange(false)}
              >
                ຍົກເລີກ
              </Button>
              <Button
                type="button"
                disabled={updateField.isPending}
                onClick={() => {
                  void handleSave();
                }}
              >
                ບັນທຶກ
              </Button>
            </DialogFooter>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
